import { BreakpointObserver } from '@angular/cdk/layout';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    Self,
    SimpleChanges
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { ImageReaderService, BrowserCapabilitiesService } from '@classifieds-sell/shared';
import { DropGridDrop, TgInput } from '@trademe/tangram';
import { TRADEME_CORE_UI_COMPACT_MAX_WIDTH } from 'libs/trademe-core-ui/src/constants/trademe-core-ui';
import { from, Observable } from 'rxjs';
import { concatMap, delay, filter, map } from 'rxjs/operators';
import {
    MAX_LISTING_PHOTOS,
    PHOTO_VALIDATION_ERROR_FILE_SIZE,
    PHOTO_VALIDATION_ERROR_IMAGE_DIMENSIONS,
    PHOTO_VALIDATION_ERROR_IMAGE_LOAD_ERROR,
    PHOTO_VALIDATION_ERROR_MIME_TYPE
} from '../constants/photo.constants';
import {
    getSafeFileNameFromFile,
    getSafeFileTypeFromFile
} from '../helpers/image-metadata';
import { EditPhoto } from '../models/edit-photo.viewmodel';
import { EditedPhotoData } from '../models/edited-photo-data';
import { Photo } from '../models/photo.viewmodel';
import { SelectedPhotoData } from '../models/selected-photo-data';
import { ValidatedPhoto } from '../models/validated-photo';
import { PhotoTransformService } from '../services/transform/photo-transform.service';

@Component({
    selector: 'tm-list-photo-management',
    templateUrl: './photo-management.component.html',
    styleUrls: ['./photo-management.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PhotoManagementComponent
    extends TgInput<never>
    implements OnInit, OnDestroy, OnChanges {
    @Input() photos: Photo[];
    @Output() photosUploading: EventEmitter<void> = new EventEmitter<void>();
    @Output() photosUploaded: EventEmitter<void> = new EventEmitter<void>();
    @Output() photoSelected = new EventEmitter<SelectedPhotoData>();
    @Output() photosUpdated = new EventEmitter<Photo[]>();
    @Output() photoTransformed = new EventEmitter<EditedPhotoData>();

    isSm: boolean;
    isFileReaderApiAvailable: boolean;

    maxPhotos: number = MAX_LISTING_PHOTOS;

    exceedLimit = false;
    beNiceAboutPhotoLimit = false;
    isContainInvalidFile = false;
    invalidFilesErrorMessages: Map<string, string[]> = new Map<
        string,
        string[]
    >([]);
    editingPhoto: EditPhoto = null;

    constructor(
        private _imageReaderService: ImageReaderService,
        private _breakpointObserver: BreakpointObserver,
        private _browserCapabilitiesService: BrowserCapabilitiesService,
        private _photoTransformService: PhotoTransformService,
        changeDetectorRef: ChangeDetectorRef,
        @Self() @Optional() control: NgControl
    ) {
        super(control, changeDetectorRef);
    }

    protected tgOnWrite(_value: never): void {
        // Not used
    }

    protected tgOnFormChange(_formState: any): void {
        // Not used
    }

    public ngOnInit(): void {
        super.ngOnInit();
        this.isFileReaderApiAvailable = this._browserCapabilitiesService.supportsFileReaderApi();

        this._breakpointObserver
            .observe([TRADEME_CORE_UI_COMPACT_MAX_WIDTH])
            .subscribe((result) => {
                this.isSmallChange(result.matches);
            });
    }

    public ngOnDestroy(): void {
        super.ngOnDestroy();
        this._destroyed$.next();
    }

    ngOnChanges(changes: SimpleChanges): void {
        const { photos } = changes;

        if (photos && !photos.isFirstChange()) {
            this.exceedLimit = !(this.photos.length < this.maxPhotos);
            if (!this.exceedLimit) {
                this.beNiceAboutPhotoLimit = false;
            }
        }
    }

    isEditMode(): boolean {
        return this.editingPhoto != null;
    }

    public onPhotosAreUploading(): void {
        this.photosUploading.emit();
    }

    public onPhotosUploaded(validatedPhotos: ValidatedPhoto[]): void {
        this.isContainInvalidFile = validatedPhotos.some(
            (f) => f.hasValidationError
        );

        if (this.isContainInvalidFile) {
            this.constructInvalidFileErrorMessage(validatedPhotos);
        }

        from(validatedPhotos)
            .pipe(
                filter((vp) => !vp.hasValidationError),
                delay(0),
                concatMap((vp: ValidatedPhoto) =>
                    this.getFileData(vp.nativeFile)
                )
            )
            .subscribe((s) => {
                if (!this.exceedLimit) {
                    this.photoSelected.emit(s);
                } else {
                    this.beNiceAboutPhotoLimit = true;
                }
            });

        this.photosUploaded.emit();
    }

    public selectPhoto(photo?: Photo) {
        this.isContainInvalidFile = false;
        this.invalidFilesErrorMessages.clear();
        this.beNiceAboutPhotoLimit = false;

        if (!!photo && !!photo.hasError) {
            this.photosUpdated.emit(this.removePhoto(photo));
        } else {
            const photoToEdit = {
                ...photo
            } as EditPhoto;

            if (photo.transformedPhoto?.crop) {
                photoToEdit.cropBox = {
                    x: photo.transformedPhoto?.crop.min.x || 0,
                    y: photo.transformedPhoto?.crop.min.y || 0,
                    width: photo.transformedPhoto?.crop.max.x || 0,
                    height: photo.transformedPhoto?.crop.max.y || 0
                };
            } else {
                photoToEdit.cropBox = null;
            }

            photoToEdit.rotation = photo.transformedPhoto?.rotation || 0;

            this.editingPhoto = photoToEdit;
        }
    }

    public deselectPhoto() {
        const editedPhotoData: EditedPhotoData = {
            photoId: this.editingPhoto.photoId,
            maxBoundingBox: this._photoTransformService.getMaxBoundingBox(
                this.editingPhoto
            )
        };
        if (
            this.editingPhoto.cropBox &&
            this._photoTransformService.isCropBoxValid(this.editingPhoto)
        ) {
            editedPhotoData.crop = {
                min: {
                    x: parseInt(this.editingPhoto.cropBox.x.toString(), 10),
                    y: parseInt(this.editingPhoto.cropBox.y.toString(), 10)
                },
                max: {
                    x: parseInt(this.editingPhoto.cropBox.width.toString(), 10),
                    y: parseInt(this.editingPhoto.cropBox.height.toString(), 10)
                }
            };
        }

        if (this.editingPhoto.rotation) {
            editedPhotoData.rotation = this.editingPhoto.rotation;
        }

        if (
            (editedPhotoData.crop != null &&
                (editedPhotoData.crop.min.x !==
                    this.editingPhoto.transformedPhoto?.crop?.min.x ||
                    editedPhotoData.crop.min.y !==
                    this.editingPhoto.transformedPhoto?.crop?.min.y ||
                    editedPhotoData.crop.max.x !==
                    this.editingPhoto.transformedPhoto?.crop?.max.x ||
                    editedPhotoData.crop.max.y !==
                    this.editingPhoto.transformedPhoto?.crop?.max.y)) ||
            (editedPhotoData.rotation != null &&
                editedPhotoData.rotation !==
                (this.editingPhoto.transformedPhoto?.rotation || 0))
        ) {
            this.photoTransformed.emit(editedPhotoData);
        }
        this.editingPhoto = null;
    }

    public isSelectedPhotoMain(): boolean {
        if (!this.editingPhoto || !this.photos || !this.photos[0]) {
            return false;
        }

        return this.photos[0].photoId === this.editingPhoto.photoId;
    }

    public showCropBox(): boolean {
        if (!this.editingPhoto) {
            return false;
        }
        return this._photoTransformService.hasCropBox(this.editingPhoto);
    }

    public setSelectedPhotoAsMain(): void {
        const editingPhotoIndex = this.photos.findIndex(
            (f) => f.photoId === this.editingPhoto.photoId
        );
        const rearrangedPhotos = this.rearrangePhotos(editingPhotoIndex, 0);
        if (rearrangedPhotos.length > 0) {
            this.photosUpdated.emit(rearrangedPhotos);
        }
    }

    public rotateSelectedPhoto(): void {
        if (!this.editingPhoto) {
            return;
        }

        const editingPhoto = this._photoTransformService.applyCropBox(
            this.editingPhoto
        );

        this.editingPhoto = this._photoTransformService.applyRotation(
            editingPhoto
        );
    }

    public cropSelectedPhoto(): void {
        if (!this.editingPhoto) {
            return;
        }

        this.editingPhoto = this._photoTransformService.toggleCropBox(
            this.editingPhoto
        );
    }

    public removeSelectedPhoto(): void {
        if (!this.editingPhoto) {
            return;
        }

        this.photosUpdated.emit(this.removePhoto(this.editingPhoto));
        this.editingPhoto = null;
    }

    public onDrop($event: DropGridDrop) {
        const rearragnedPhotos = this.rearrangePhotos(
            $event.previousIndex,
            $event.currentIndex
        );
        if (rearragnedPhotos.length > 0) {
            this.photosUpdated.emit(rearragnedPhotos);
        }
    }

    public getErrorDetail(key: string) {
        switch (key) {
            case PHOTO_VALIDATION_ERROR_MIME_TYPE:
                return ' because the type of photo is not supported (supported types: PNGs, JPGs, BMPs, or GIFs)';
            case PHOTO_VALIDATION_ERROR_FILE_SIZE:
                return ' because photo size must be smaller than 5MB';
            case PHOTO_VALIDATION_ERROR_IMAGE_DIMENSIONS:
                return ' because photo must be at least 500 pixels tall and wide';
            case PHOTO_VALIDATION_ERROR_IMAGE_LOAD_ERROR:
                return ' because there is a problem with loading the photo';
        }
        return '';
    }

    private isSmallChange(isSmall: boolean) {
        this.isSm = isSmall;
        this._changeDetectorRef.markForCheck();
    }

    private getFileData(file: File): Observable<SelectedPhotoData> {
        return this._imageReaderService.readImageDataFromFileAsync(file).pipe(
            map((image) => {
                return {
                    fileName: getSafeFileNameFromFile(file),
                    fileType: getSafeFileTypeFromFile(file),
                    photoData: image.data
                } as SelectedPhotoData;
            })
        );
    }

    private rearrangePhotos(fromIndex: number, toIndex: number): Photo[] {
        if (fromIndex === toIndex) {
            return [];
        }

        const photos = [...this.photos];
        const itemToMove = photos.splice(fromIndex, 1);
        photos.splice(toIndex, 0, ...itemToMove);

        return photos;
    }

    private removePhoto(photo: Photo): Photo[] {
        const photos = [...this.photos];
        const photoIndex = photos.findIndex((f) => f.photoId === photo.photoId);
        photos.splice(photoIndex, 1);

        return photos;
    }

    private constructInvalidFileErrorMessage(
        validatedPhotos: ValidatedPhoto[]
    ): void {
        validatedPhotos.forEach((f) => {
            if (f.hasValidationError) {
                if (this.invalidFilesErrorMessages.has(f.validationError.key)) {
                    this.invalidFilesErrorMessages
                        .get(f.validationError.key)
                        .push(f.nativeFile.name);
                } else {
                    this.invalidFilesErrorMessages.set(f.validationError.key, [
                        f.nativeFile.name
                    ]);
                }
                f.validationError.getMessage();
            }
        });
    }
}
